import SqOneTrust, { ALERT_BOX_CLOSED_COOKIE_KEY } from "@square/one-trust";
import Cookies from "js-cookie";
import { isTestEnvironment } from "./libs/shared/util-environment/dist/index.js";
import { OneTrustIntegrationScripts } from "./OneTrustIntegrationScripts.js";
var OneTrustConsent = /* @__PURE__ */ ((OneTrustConsent2) => {
  OneTrustConsent2["STRICTLY_NECESSARY"] = "C0001";
  OneTrustConsent2["PERFORMANCE"] = "C0002";
  OneTrustConsent2["FUNCTIONAL"] = "C0003";
  OneTrustConsent2["TARGETING"] = "C0004";
  return OneTrustConsent2;
})(OneTrustConsent || {});
class EnvoyConsentHeader {
  "X-Block-Cookies";
  "X-Allow-Cookies";
}
const X_ALLOW_COOKIES = "X-Allow-Cookies";
const X_BLOCK_COOKIES = "X-Block-Cookies";
const X_BLOCK_COOKIES_DEFAULT_VALUE = "true";
const X_CSRF_Token = "X-CSRF-Token";
const POST_NEW_ONE_TRUST_INSTANCE = "dashboardSqOneTrustInstanceChangedEvent";
const ONE_TRUST_PREFERENCE_CHANGE = "dashboardOneTrustPreferencesChangedEvent";
const ONE_TRUST_ALERT_COOKIE = "OptanonAlertBoxClosed";
function getOneTrustActiveGroups() {
  return window.OnetrustActiveGroups;
}
function getOneTrustConsentModel() {
  return window.OneTrust?.GetDomainData?.()?.ConsentModel?.Name;
}
function hasConsentBeenSet() {
  const noConsentFound = !getOneTrustActiveGroups();
  const hasAutoConsented = /C000[^1]/.test(getOneTrustActiveGroups());
  const isInOptOutRegion = getOneTrustConsentModel() === "opt-out";
  const hasManuallyConsented = document.cookie.includes(ONE_TRUST_ALERT_COOKIE);
  return Boolean(noConsentFound || hasAutoConsented || isInOptOutRegion || hasManuallyConsented);
}
function hasConsent(consentLevel) {
  if (!consentLevel) {
    throw new Error(
      "Consent level must be provided for OneTrust. Consent levels are available in `@squareup/service-one-trust`."
    );
  }
  return (
    // eslint-disable-next-line square/no-process-env-in-libraries
    process.env.NODE_ENV === "test" || window.SqOneTrust?.groupConsentHash[consentLevel] === "1"
  );
}
function generateEnvoyConsentHeader() {
  if (process.env.NODE_ENV === "test") {
    return {};
  }
  const envoyConsentDefaultHeader = {
    [X_BLOCK_COOKIES]: X_BLOCK_COOKIES_DEFAULT_VALUE
  };
  const activeGroups = getOneTrustActiveGroups();
  if (hasConsentBeenSet() && activeGroups) {
    return {
      ...envoyConsentDefaultHeader,
      [X_ALLOW_COOKIES]: activeGroups
    };
  }
  return envoyConsentDefaultHeader;
}
function onFirstConsent(consentLevel, callback) {
  if (hasConsent(consentLevel)) {
    callback();
    return () => {
    };
  }
  function executeAfterOneTrust() {
    if (hasConsent(consentLevel)) {
      callback();
      document.removeEventListener(POST_NEW_ONE_TRUST_INSTANCE, executeAfterOneTrust);
    }
  }
  document.addEventListener(POST_NEW_ONE_TRUST_INSTANCE, executeAfterOneTrust);
  return () => {
    document.removeEventListener(POST_NEW_ONE_TRUST_INSTANCE, executeAfterOneTrust);
  };
}
function onFirstRejection(consentLevel, callback) {
  if (!hasConsent(consentLevel) && hasConsentBeenSet()) {
    callback();
    return () => {
    };
  }
  function executeAfterOneTrust() {
    if (!hasConsent(consentLevel) && hasConsentBeenSet()) {
      callback();
      document.removeEventListener(POST_NEW_ONE_TRUST_INSTANCE, executeAfterOneTrust);
    }
  }
  document.addEventListener(POST_NEW_ONE_TRUST_INSTANCE, executeAfterOneTrust);
  return () => {
    document.removeEventListener(POST_NEW_ONE_TRUST_INSTANCE, executeAfterOneTrust);
  };
}
function getConsent(consentLevel) {
  return new Promise((resolve) => {
    const cleanup = {
      consent: void 0,
      rejection: void 0
    };
    cleanup.consent = onFirstConsent(consentLevel, () => {
      if (cleanup.rejection) {
        cleanup.rejection();
      }
      resolve(true);
    });
    cleanup.rejection = onFirstRejection(consentLevel, () => {
      if (cleanup.consent) {
        cleanup.consent();
      }
      resolve(false);
    });
  });
}
function showOneTrustModal() {
  window.OneTrust?.ToggleInfoDisplay();
}
function isInEU() {
  return window.SqOneTrust?.userLocatedInEU;
}
const FUNCTIONALITY_COOKIES_BLOCKED_DEFAULT = false;
const ANALYTICS_COOKIES_BLOCKED_DEFAULT = false;
const RETARGETING_COOKIES_BLOCKED_DEFAULT = false;
const NECESSARY_COOKIES_BLOCKED_DEFAULT = false;
const GROUP_CONSENT_HASH_DEFAULT = {
  C0001: "1",
  C0002: "1",
  C0003: "1",
  C0004: "1"
};
class SqOneTrustService {
  /**
   * Subset of the SqOneTrust instance that is always kept in-sync with the one
   * in window. If window.SqOneTrust is not present, the properties will
   * remain in its default values.
   */
  _sqOneTrust = {
    strictlyNecessaryCookiesBlocked: NECESSARY_COOKIES_BLOCKED_DEFAULT,
    functionalityCookiesBlocked: FUNCTIONALITY_COOKIES_BLOCKED_DEFAULT,
    performanceAndAnalyticsCookiesBlocked: ANALYTICS_COOKIES_BLOCKED_DEFAULT,
    retargetingOrAdvertisingCookiesBlocked: RETARGETING_COOKIES_BLOCKED_DEFAULT,
    groupConsentHash: GROUP_CONSENT_HASH_DEFAULT
  };
  constructor() {
    if (!getOneTrustActiveGroups() && !isTestEnvironment()) {
      this._updateOneTrustPreferences(true);
      return;
    }
    this._updateOneTrustPreferences();
    window.OptanonWrapper = () => {
      document.dispatchEvent(new CustomEvent(ONE_TRUST_PREFERENCE_CHANGE));
    };
    document.addEventListener(ONE_TRUST_PREFERENCE_CHANGE, this._onOneTrustPreferenceChange);
  }
  _onOneTrustPreferenceChange = () => {
    this._updateOneTrustPreferences();
  };
  _updateOneTrustPreferences(scriptFailedToLoad) {
    window.SqOneTrust = new SqOneTrust({ passSquareGeoToOneTrust: true });
    if (scriptFailedToLoad && !this._cookieIsValid()) {
      window.SqOneTrust.groupConsentHash = {
        [
          "C0001"
          /* STRICTLY_NECESSARY */
        ]: "1",
        [
          "C0002"
          /* PERFORMANCE */
        ]: "0",
        [
          "C0003"
          /* FUNCTIONAL */
        ]: "0",
        [
          "C0004"
          /* TARGETING */
        ]: "0"
      };
    }
    this._ensureAlertBoxCookieExists();
    this._populatePreferenceFromWindow();
    document.dispatchEvent(new CustomEvent(POST_NEW_ONE_TRUST_INSTANCE));
  }
  // Check if the OptanonAlertBoxClosed cookie is set to a valid date of less than a year ago.
  _cookieIsValid() {
    const alertCookieDate = Cookies.get(ONE_TRUST_ALERT_COOKIE);
    if (!alertCookieDate) {
      return true;
    }
    const alertInteractionDate = new Date(alertCookieDate);
    if (Number.isNaN(alertInteractionDate.getDate())) {
      return false;
    }
    const currentDate = /* @__PURE__ */ new Date();
    const diff = currentDate.getTime() - alertInteractionDate.getTime();
    const yearDiff = diff / (1e3 * 60 * 60 * 24 * 365);
    return yearDiff < 1;
  }
  /**
   * Populate our instance of SqOneTrust with the one in window. This is
   * called when the instance is first created, and subsequently when
   * the event POST_NEW_ONE_TRUST_INSTANCE is triggered by our host apps.
   */
  _populatePreferenceFromWindow = () => {
    if (window.SqOneTrust) {
      this._sqOneTrust.strictlyNecessaryCookiesBlocked = window.SqOneTrust.strictlyNecessaryCookiesBlocked;
      this._sqOneTrust.functionalityCookiesBlocked = window.SqOneTrust.functionalityCookiesBlocked;
      this._sqOneTrust.performanceAndAnalyticsCookiesBlocked = window.SqOneTrust.performanceAndAnalyticsCookiesBlocked;
      this._sqOneTrust.retargetingOrAdvertisingCookiesBlocked = window.SqOneTrust.retargetingOrAdvertisingCookiesBlocked;
      this._sqOneTrust.groupConsentHash = window.SqOneTrust.groupConsentHash;
    }
  };
  /**
   * Certain browser extensions "I don't care about cookies" for example, broke header loading after consent.
   * This sets the cookie `OptanonAlertBoxClosed` if an external script is blocking the Onetrust modal.
   * https://github.com/squareup/appointments/blob/c2195edd19c45b163b37373e221a2d87fbe9a6d7/merchant/app/instance-initializers/onetrust.js#L35
   */
  _ensureAlertBoxCookieExists = () => {
    const existingCookie = Cookies.get(ALERT_BOX_CLOSED_COOKIE_KEY);
    if (existingCookie) {
      return;
    }
    const oneTrustSdkElement = document.getElementById("onetrust-consent-sdk");
    const isHidden = oneTrustSdkElement ? window.getComputedStyle(oneTrustSdkElement).visibility === "hidden" : false;
    if (isHidden) {
      Cookies.set(ALERT_BOX_CLOSED_COOKIE_KEY, (/* @__PURE__ */ new Date()).toISOString());
    }
  };
  /**
   * Get the SqOneTrust instance as a Proxy so that it can be stored elsewhere in
   * the code while still keeping it updated whenever we re-populate from window
   * since we are passing by reference. However, as we intercept the setter here,
   * no where outside this controller can mutate the properties of the instance.
   */
  getSqOneTrust = () => {
    function getter(target, key) {
      if (key === "strictlyNecessaryCookiesBlocked") {
        return target.strictlyNecessaryCookiesBlocked;
      }
      if (key === "functionalityCookiesBlocked") {
        return target.functionalityCookiesBlocked;
      }
      if (key === "performanceAndAnalyticsCookiesBlocked") {
        return target.performanceAndAnalyticsCookiesBlocked;
      }
      if (key === "retargetingOrAdvertisingCookiesBlocked") {
        return target.retargetingOrAdvertisingCookiesBlocked;
      }
      return false;
    }
    return new Proxy(this._sqOneTrust, {
      get: getter,
      set: getter
    });
  };
  /**
   * Get a list of headers to append to requests. Documentation can be found here:
   * https://docs.google.com/document/d/141w7iDe38ygMR8wqeJuPH0rGJ-EyG4QUs3AN4o4K9jE/edit#
   * Essentially, X-Block-Cookies is a backend flag for whether GDPR should be
   * enforced at all. X-Allow-Cookies will be present once the user consents
   * to any type of cookie (at a minimum, this includes strictly necessary, i.e. C0001).
   */
  getCookieFilterHeaders = () => {
    let allowHeaderValue = "";
    const allowedCookies = [];
    for (const cookieName of Object.keys(this._sqOneTrust.groupConsentHash)) {
      if (this._sqOneTrust.groupConsentHash[cookieName] === "1") {
        allowedCookies.push(cookieName);
      }
    }
    allowHeaderValue = allowedCookies.join(", ");
    return {
      "X-Allow-Cookies": allowHeaderValue,
      "X-Block-Cookies": "true"
    };
  };
}
export {
  ANALYTICS_COOKIES_BLOCKED_DEFAULT,
  EnvoyConsentHeader,
  FUNCTIONALITY_COOKIES_BLOCKED_DEFAULT,
  GROUP_CONSENT_HASH_DEFAULT,
  NECESSARY_COOKIES_BLOCKED_DEFAULT,
  ONE_TRUST_ALERT_COOKIE,
  OneTrustConsent,
  OneTrustIntegrationScripts,
  POST_NEW_ONE_TRUST_INSTANCE,
  RETARGETING_COOKIES_BLOCKED_DEFAULT,
  X_ALLOW_COOKIES,
  X_BLOCK_COOKIES,
  X_BLOCK_COOKIES_DEFAULT_VALUE,
  X_CSRF_Token,
  SqOneTrustService as default,
  generateEnvoyConsentHeader,
  getConsent,
  hasConsent,
  hasConsentBeenSet,
  isInEU,
  onFirstConsent,
  onFirstRejection,
  showOneTrustModal
};
//# sourceMappingURL=index.js.map
